import { DestroyRef, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { getConfirmationCheckboxesInputs, selectIsTicketAssignmentStepVisible } from '@app/app.reducer';
import {
  AllPersonsAndFormValues,
  AssignedPersonFormValues,
  AssignedPersonTariffFormValues
} from '@core/data-access/models/form/form-provider.model';
import { FormName, FormValues, QuestionnaireFormValues } from '@core/data-access/models/form/form.model';
import { FormMediator } from '@core/data-access/services/form/form-mediator.service';
import { BuyerBillingAddressFormState } from '@core/features/buyer-information/models/buyer-billing-address-form.model';
import { BuyerInformationFormState } from '@core/features/buyer-information/models/buyer-information-form.model';
import { BuyerPrivacyPolicyFormState } from '@core/features/buyer-information/models/buyer-privacy-policy-form.model';
import { BuyerQuestionnaireFormState } from '@core/features/buyer-questionnaire/models/buyer-questionnaire-fom.model';
import {
  TicketHolderFormState,
  TicketHolderFormStates
} from '@core/features/ticket-assignment/models/ticket-holder-form.model';
import { TicketHolderQuestionnaireFormStates } from '@core/features/ticket-assignment/models/ticket-holder-questionnaire-fom.model';
import { sanitizeAndNormalizeFormValues, sanitizeAndNormalizeQuestionnaireFormValues } from '@core/utils/form-helpers';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TariffUuid } from '@products/models/tariff.model';
import { isAnonymousProductBooked, selectBookedTariffUuids } from '@store/products/booking/booking.selectors';
import { fromPairs, isEmpty, isNil, isUndefined, reduce } from 'lodash';
import { combineLatest, filter, first, iif, map, Observable, of, share, switchMap, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormProvider {
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #store = inject(Store);
  readonly #formMediator = inject(FormMediator);

  #assignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$ = (
    translateService?: TranslateService
  ) => this.assignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$(translateService);
  #buyerInformationPersonAndFormValues$ = (translateService?: TranslateService) =>
    this.buyerInformationPersonAndFormValues$(translateService);
  #buyerBillingAddressPersonAndFormValues$ = (translateService?: TranslateService) =>
    this.buyerBillingAddressPersonAndFormValues$(translateService);
  #buyerPrivacyPolicyPersonAndFormValues$ = (translateService?: TranslateService) =>
    this.buyerPrivacyPolicyPersonAndFormValues$(translateService);
  #buyerQuestionnairePersonAndFormValues$ = this.buyerQuestionnairePersonAndFormValues$();
  #ticketHolderPersonAndFormValues$ = (translateService?: TranslateService) =>
    this.ticketHolderPersonAndFormValues$(translateService);
  #ticketHolderQuestionnairePersonAndFormValues$ = () => this.ticketHolderQuestionnairePersonAndFormValues$();
  #confirmationCheckboxesFormValues$ = this.confirmationCheckboxesFormValues$();
  #buyerInformationAndTicketHolderPersonsAndFormValues$ = (translateService?: TranslateService) =>
    this.buyerInformationAndTicketHolderPersonsAndFormValues$(translateService);
  #allPersonsAndFormValues$ = (translateService?: TranslateService) => this.allPersonsAndFormValues$(translateService);

  getAssignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$(
    translateService?: TranslateService
  ) {
    return this.#assignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$(translateService);
  }

  getBuyerInformationPersonAndFormValues$(translateService?: TranslateService) {
    return this.#buyerInformationPersonAndFormValues$(translateService);
  }

  getBuyerBillingAddressPersonAndFormValues$(translateService?: TranslateService) {
    return this.#buyerBillingAddressPersonAndFormValues$(translateService);
  }

  getBuyerPrivacyPolicyPersonAndFormValues$(translateService?: TranslateService) {
    return this.#buyerPrivacyPolicyPersonAndFormValues$(translateService);
  }

  getBuyerQuestionnairePersonAndFormValues$() {
    return this.#buyerQuestionnairePersonAndFormValues$;
  }

  getTicketHolderPersonAndFormValues$(translateService?: TranslateService) {
    return this.#ticketHolderPersonAndFormValues$(translateService);
  }

  getTicketHolderQuestionnairePersonAndFormValues$() {
    return this.#ticketHolderQuestionnairePersonAndFormValues$;
  }

  getConfirmationCheckboxesFormValues$() {
    return this.#confirmationCheckboxesFormValues$;
  }

  getBuyerInformationAndTicketHolderPersonsAndFormValues$(translateService?: TranslateService) {
    return this.#buyerInformationAndTicketHolderPersonsAndFormValues$(translateService);
  }

  getAllPersonsAndFormValues$(translateService?: TranslateService) {
    return this.#allPersonsAndFormValues$(translateService);
  }

  private assignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$(
    translateService?: TranslateService
  ): Observable<AssignedPersonTariffFormValues> {
    return runInInjectionContext(this.#injector, () => {
      const buyerInformationFormState$ = toObservable<BuyerInformationFormState>(
        this.#formMediator.getFormState(FormName.BuyerInformation)
      );
      const ticketHolderFormStates$ = toObservable<TicketHolderFormStates>(
        this.#formMediator.getFormState(FormName.TicketHolder)
      );

      return this.#store.pipe(
        select(selectIsTicketAssignmentStepVisible),
        withLatestFrom(this.#store.select(isAnonymousProductBooked)),
        switchMap(([isTicketHolderStepVisible, isAnonymousProductBooked]) =>
          iif(
            () => isTicketHolderStepVisible && !isAnonymousProductBooked,
            ticketHolderFormStates$.pipe(
              filter((ticketHolderFormStates) => !isEmpty(ticketHolderFormStates)),
              map((ticketHolderFormStates) => fromPairs([...ticketHolderFormStates]))
            ),
            combineLatest({
              bookedTariffUuids: this.#store.select(selectBookedTariffUuids),
              buyerInformationFormState: buyerInformationFormState$
            }).pipe(
              filter(
                ({ bookedTariffUuids, buyerInformationFormState }) =>
                  !isEmpty(bookedTariffUuids) && !isEmpty(buyerInformationFormState)
              ),
              map(({ bookedTariffUuids, buyerInformationFormState }) =>
                reduce(
                  bookedTariffUuids,
                  (groupedBuyerInformationFormState, tariffUuid) => (
                    (groupedBuyerInformationFormState[tariffUuid] = buyerInformationFormState),
                    groupedBuyerInformationFormState
                  ),
                  {}
                )
              )
            )
          )
        ),
        filter((formStatesGroupedByTariffUuid) => !isEmpty(formStatesGroupedByTariffUuid)),
        map((formStatesGroupedByTariffUuid) =>
          reduce(
            formStatesGroupedByTariffUuid,
            (
              assignedPersonTariffFormValues,
              formState: BuyerInformationFormState | TicketHolderFormState,
              tariffUuid: TariffUuid
            ) => {
              assignedPersonTariffFormValues[tariffUuid] = {
                person: formState?.person,
                formValues: sanitizeAndNormalizeFormValues(formState?.form?.formGroup?.getRawValue(), translateService)
              };

              return assignedPersonTariffFormValues;
            },
            {}
          )
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      );
    });
  }

  private buyerInformationPersonAndFormValues$(
    translateService?: TranslateService
  ): Observable<AssignedPersonFormValues> {
    return runInInjectionContext(this.#injector, () =>
      toObservable<BuyerInformationFormState>(this.#formMediator.getFormState(FormName.BuyerInformation)).pipe(
        first((formStates) => !isNil(formStates) || isUndefined(formStates)),
        map((formState) =>
          isUndefined(formState)
            ? { person: null, formValues: {} }
            : {
                person: formState?.person,
                formValues: sanitizeAndNormalizeFormValues<FormValues>(
                  formState?.form?.formGroup?.getRawValue(),
                  translateService
                )
              }
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }

  private buyerBillingAddressPersonAndFormValues$(
    translateService?: TranslateService
  ): Observable<AssignedPersonFormValues> {
    return runInInjectionContext(this.#injector, () =>
      toObservable<BuyerBillingAddressFormState>(this.#formMediator.getFormState(FormName.BuyerBillingAddress)).pipe(
        first((formState) => !isNil(formState) || isUndefined(formState)),
        map((formState) =>
          isUndefined(formState)
            ? { person: null, formValues: {} }
            : {
                person: formState?.person,
                formValues: sanitizeAndNormalizeFormValues<FormValues>(
                  formState?.form?.formGroup?.getRawValue(),
                  translateService
                )
              }
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }

  private buyerPrivacyPolicyPersonAndFormValues$(
    translateService?: TranslateService
  ): Observable<AssignedPersonFormValues> {
    return runInInjectionContext(this.#injector, () =>
      toObservable<BuyerPrivacyPolicyFormState>(this.#formMediator.getFormState(FormName.BuyerPrivacyPolicy)).pipe(
        first((formState) => !isNil(formState) || isUndefined(formState)),
        map((formState) =>
          isUndefined(formState)
            ? { person: null, formValues: {} }
            : {
                person: formState?.person,
                formValues: sanitizeAndNormalizeFormValues<FormValues>(
                  formState?.form?.formGroup?.getRawValue(),
                  translateService
                )
              }
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }

  private buyerQuestionnairePersonAndFormValues$(): Observable<AssignedPersonFormValues<QuestionnaireFormValues>> {
    return runInInjectionContext(this.#injector, () =>
      toObservable<BuyerQuestionnaireFormState>(this.#formMediator.getFormState(FormName.BuyerQuestionnaire)).pipe(
        first((formStates) => !isNil(formStates) || isUndefined(formStates)),
        map((formState) =>
          isUndefined(formState)
            ? { person: null, formValues: {} }
            : {
                person: formState?.person,
                formValues: sanitizeAndNormalizeQuestionnaireFormValues<QuestionnaireFormValues>(
                  formState?.form?.formGroup?.value
                )
              }
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }

  private ticketHolderPersonAndFormValues$(
    translateService?: TranslateService
  ): Observable<AssignedPersonTariffFormValues> {
    return runInInjectionContext(this.#injector, () =>
      toObservable<TicketHolderFormStates>(this.#formMediator.getFormState(FormName.TicketHolder)).pipe(
        first((formStates) => !isNil(formStates) || isUndefined(formStates)),
        map((formStates) =>
          isUndefined(formStates)
            ? {}
            : reduce(
                fromPairs([...formStates]),
                (ticketHoldersPersonAndFormValues, { person, form }, tariffUuid) => {
                  ticketHoldersPersonAndFormValues[tariffUuid] = {
                    person,
                    formValues: sanitizeAndNormalizeFormValues<FormValues>(
                      form?.formGroup?.getRawValue(),
                      translateService
                    )
                  };

                  return ticketHoldersPersonAndFormValues;
                },
                {}
              )
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }

  private ticketHolderQuestionnairePersonAndFormValues$(): Observable<
    AssignedPersonTariffFormValues<QuestionnaireFormValues>
  > {
    return runInInjectionContext(this.#injector, () =>
      toObservable<TicketHolderQuestionnaireFormStates>(
        this.#formMediator.getFormState(FormName.TicketHolderQuestionnaire)
      ).pipe(
        first((formStates) => !isNil(formStates) || isUndefined(formStates)),
        map((formStates) =>
          isUndefined(formStates)
            ? {}
            : reduce(
                fromPairs([...formStates]),
                (ticketHoldersPersonAndFormValues, { person, form }, tariffUuid) => {
                  ticketHoldersPersonAndFormValues[tariffUuid] = {
                    person,
                    formValues: sanitizeAndNormalizeQuestionnaireFormValues<QuestionnaireFormValues>(
                      form?.formGroup?.value
                    )
                  };

                  return ticketHoldersPersonAndFormValues;
                },
                {}
              )
        ),
        share(),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }

  private confirmationCheckboxesFormValues$(): Observable<AssignedPersonFormValues> {
    return this.#store.pipe(
      select(getConfirmationCheckboxesInputs),
      switchMap(({ list }) =>
        iif(
          () => isNil(list),
          of({ person: null, formValues: {} }),
          of({
            person: null,
            formValues: sanitizeAndNormalizeFormValues<FormValues>(
              reduce(
                list,
                (formValues, form) => {
                  const key = form.key;

                  formValues[key] = reduce(
                    form.options,
                    (options, { key, value }) => ((options[key] = value), options),
                    {}
                  );

                  return formValues;
                },
                {}
              )
            )
          })
        )
      ),
      share(),
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private buyerInformationAndTicketHolderPersonsAndFormValues$(translateService?: TranslateService) {
    return combineLatest({
      [FormName.BuyerInformation]: this.buyerInformationPersonAndFormValues$(translateService),
      [FormName.TicketHolder]: this.ticketHolderPersonAndFormValues$(translateService)
    }).pipe(
      map(({ buyerInformation, ticketHolder }) => ({
        buyerInformation,
        ticketHolder
      })),
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private allPersonsAndFormValues$(translateService?: TranslateService): Observable<AllPersonsAndFormValues> {
    return combineLatest({
      [FormName.BuyerInformation]: this.buyerInformationPersonAndFormValues$(translateService),
      [FormName.BuyerBillingAddress]: this.buyerBillingAddressPersonAndFormValues$(translateService),
      [FormName.BuyerPrivacyPolicy]: this.buyerPrivacyPolicyPersonAndFormValues$(translateService),
      [FormName.BuyerQuestionnaire]: this.buyerQuestionnairePersonAndFormValues$(),
      [FormName.TicketHolder]:
        this.assignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$(translateService),
      [FormName.TicketHolderQuestionnaire]: this.ticketHolderQuestionnairePersonAndFormValues$(),
      [FormName.ConfirmationCheckboxes]: this.confirmationCheckboxesFormValues$()
    }).pipe(
      first(),
      map(
        ({
          buyerInformation,
          buyerBillingAddress,
          buyerPrivacyPolicy,
          buyerQuestionnaire,
          ticketHolder,
          ticketHolderQuestionnaire,
          confirmationCheckboxes
        }) => ({
          buyerInformation,
          buyerBillingAddress,
          buyerPrivacyPolicy,
          buyerQuestionnaire,
          ticketHolder,
          ticketHolderQuestionnaire,
          confirmationCheckboxes
        })
      ),
      share(),
      takeUntilDestroyed(this.#destroyRef)
    );
  }
}
